import React, { useLayoutEffect } from 'react';
import { StyleSheet, View, } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, LineSeparator, rem, SCREEN_BOTTOM_PADDING, SettingsItem, SettingsSection, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import CardRow from 'design-system/CardRow';
import { IconLocation, IconUser } from 'design-system/icons';
import { IconVote } from 'design-system/icons/IconVote';
import InfoCard from 'design-system/InfoCard';
import ListEmptyView from 'design-system/ListEmptyView';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { formatDateForItem } from 'utils';
import useEnsureData from 'hooks/useEnsureData';
import { isWeb } from '../../../constants';
import { selectCreditReportAddressLinks, selectCreditReportElectoralRoll, selectCreditReportFinancialConnections, selectCreditReportOtherNames, selectCreditReportPersonalInformation, selectHasCreditReport, } from '../selectors';
import useGetCreditReport from '../hooks/useGetCreditReport';
import { getElectoralRollStatusLabel } from '../utils';
import CreditReportLenderLogo from '../components/CreditReportLenderLogo';
import DisputeActionCard from '../components/DisputeActionCard';
const strings = {
    personal: {
        headerTitle: 'Personal details',
        headerSubtitle: 'These are your personal details on your TransUnion credit report',
        emptyTitle: 'No personal details reported',
        sections: {
            name: 'Name',
            address: 'Current address',
            electoralRegister: 'Electoral register',
        },
        toolTip: 'We use the personal details you’ve registered with Emma to fetch your report from TransUnion. If your details have changed, please update them in your Emma settings',
    },
    address: {
        headerTitle: 'Linked addresses',
        headerSubtitle: 'Linked addresses are created when a lender provides a new address for you. In most cases there will be previous address and/or correspondence addresses',
        emptyTitle: 'No linked addresses reported',
        itemPrefix: 'Address link',
        sections: {
            from: 'From',
            to: 'To',
            recent: 'Most recent confirmation',
            earliest: 'Earliest confirmation',
            source: 'Most recent source',
        },
    },
    financialConnections: {
        headerTitle: 'Financial connections',
        headerSubtitle: 'Details of people with whom you’ve shared a financial connection',
        emptyTitle: 'No financial connections reported',
        itemPrefix: 'Connection',
        sections: {
            lastConfirmed: 'Last confirmed',
            confirmed: 'Confirmed on',
            source: 'Source',
        },
    },
    otherNames: {
        headerTitle: 'Other names',
        headerSubtitle: 'Any other names you’ve used, such as a maiden name or previous variations, that might appear on your financial accounts',
        emptyTitle: 'No other names reported',
        itemPrefix: 'Name',
        sections: {
            alias: 'Your name',
            linkName: 'Appeared as',
            source: 'Source',
            lastConfirmed: 'Confirmed on',
            confirmed: 'Originated on',
        },
    },
};
const indexKeyExtractor = (_, index) => String(index);
const SourceItem = ({ source, iconUrl, }) => (<View style={styles.sourceContainer}>
    <Text Text-14>{source}</Text>
    <CreditReportLenderLogo size="small" name={source} iconUrl={iconUrl}/>
  </View>);
const SectionSubtitle = ({ text, pulledIn, }) => (<View style={[
        styles.subtitleContainer,
        pulledIn && styles.subtitleContainerPulledIn,
    ]}>
    <Text TextThin-16 Gray>
      {text}
    </Text>
  </View>);
const PersonalDetails = ({ scrollHandler, contentContainerStyle, }) => {
    const personalDetails = useAppSelector(selectCreditReportPersonalInformation);
    const electoralRoll = useAppSelector(selectCreditReportElectoralRoll);
    const name = personalDetails?.name;
    const currentAddress = personalDetails?.currentAddress;
    const electoralLabel = getElectoralRollStatusLabel(electoralRoll?.currentStatus);
    const hasAnyRow = !!name || !!currentAddress || !!electoralLabel;
    return (<Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle}>
      <SectionSubtitle text={strings.personal.headerSubtitle} pulledIn/>
      {!hasAnyRow ? (<ListEmptyView happy onCard title={strings.personal.emptyTitle} style={styles.listEmptyView}/>) : (<SettingsSection>
          {!!name && (<SettingsItem title={name} topDescription={strings.personal.sections.name} icon={<IconUser gradientKey="darkPurple"/>}/>)}
          {!!currentAddress && (<SettingsItem title={currentAddress} topDescription={strings.personal.sections.address} icon={<IconLocation alt gradientKey="yellow"/>}/>)}
          {!!electoralLabel && (<SettingsItem title={electoralLabel} topDescription={strings.personal.sections.electoralRegister} icon={<IconVote alt gradientKey="apricot"/>}/>)}
        </SettingsSection>)}
      <InfoCard style={styles.infoCardMargin}>
        <Text TextThin-14 Gray>
          {strings.personal.toolTip}
        </Text>
      </InfoCard>
      <DisputeActionCard params={{ category: 'electoralRoll' }}/>
    </Animated.ScrollView>);
};
const DetailSeparator = () => (<>
    <LineSeparator style={styles.lineSeparator}/>
    <Spacer.H4 />
  </>);
const AddressLinkItem = React.memo(({ address, index }) => {
    if (!address)
        return null;
    const { addressFrom, addressTo, mostRecentSource, iconUrl, earliestConfirmationOn, lastConfirmationOn, } = address;
    const hasItems = !!addressFrom || !!addressTo;
    const hasRows = !!lastConfirmationOn || !!earliestConfirmationOn || !!mostRecentSource;
    return (<SettingsSection name={`${strings.address.itemPrefix} ${index + 1}`}>
        {!!addressFrom && (<SettingsItem title={addressFrom} topDescription={strings.address.sections.from} icon={<IconLocation alt gradientKey="yellow"/>}/>)}
        {!!addressTo && (<SettingsItem title={addressTo} topDescription={strings.address.sections.to} icon={<IconLocation alt gradientKey="yellow"/>}/>)}
        {hasItems && hasRows ? <DetailSeparator /> : null}
        {!!lastConfirmationOn && (<CardRow title={strings.address.sections.recent} info={formatDateForItem(lastConfirmationOn)} small tableSpacing/>)}
        {!!earliestConfirmationOn && (<CardRow title={strings.address.sections.earliest} info={formatDateForItem(earliestConfirmationOn)} small tableSpacing/>)}
        {!!mostRecentSource && (<CardRow title={strings.address.sections.source} info={<SourceItem source={mostRecentSource} iconUrl={iconUrl}/>} small tableSpacing/>)}
        {hasRows ? <Spacer.H4 /> : null}
      </SettingsSection>);
});
const renderAddressLink = ({ item, index }) => (<AddressLinkItem address={item} index={index}/>);
const Addresses = ({ scrollHandler, contentContainerStyle, }) => {
    const linkedAddresses = useAppSelector(selectCreditReportAddressLinks);
    return (<Animated.FlatList data={linkedAddresses} keyExtractor={indexKeyExtractor} renderItem={renderAddressLink} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.address.headerSubtitle} pulledIn/>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.address.emptyTitle} style={styles.listEmptyView}/>} ListFooterComponent={<DisputeActionCard params={{ category: 'addressLinks' }}/>}/>);
};
const FinancialConnectionItem = React.memo(({ connection, index }) => {
    if (!connection)
        return null;
    const { name, source, iconUrl, lastConfirmedOn, confirmedOn } = connection;
    const hasRows = !!lastConfirmedOn || !!confirmedOn || !!source;
    return (<SettingsSection name={`${strings.financialConnections.itemPrefix} ${index + 1}`}>
      {!!name && (<SettingsItem title={name} icon={<IconUser alt gradientKey="orange"/>}/>)}
      {!!name && hasRows ? <DetailSeparator /> : null}
      {!!lastConfirmedOn && (<CardRow title={strings.financialConnections.sections.lastConfirmed} info={formatDateForItem(lastConfirmedOn)} small tableSpacing/>)}
      {!!confirmedOn && (<CardRow title={strings.financialConnections.sections.confirmed} info={formatDateForItem(confirmedOn)} small tableSpacing/>)}
      {!!source && (<CardRow title={strings.financialConnections.sections.source} info={<SourceItem source={source} iconUrl={iconUrl}/>} small tableSpacing/>)}
      {hasRows ? <Spacer.H4 /> : null}
    </SettingsSection>);
});
const renderFinancialConnection = ({ item, index, }) => <FinancialConnectionItem connection={item} index={index}/>;
const FinancialConnections = ({ scrollHandler, contentContainerStyle, }) => {
    const financialConnections = useAppSelector(selectCreditReportFinancialConnections);
    return (<Animated.FlatList data={financialConnections} keyExtractor={indexKeyExtractor} renderItem={renderFinancialConnection} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.financialConnections.headerSubtitle} pulledIn/>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.financialConnections.emptyTitle} style={styles.listEmptyView}/>} ListFooterComponent={<DisputeActionCard params={{ category: 'financialConnections' }}/>}/>);
};
const OtherNameItem = React.memo(({ names, index }) => {
    if (!names)
        return null;
    const { alias, linkName, source, iconUrl, lastConfirmedOn, confirmedOn } = names;
    const hasItems = !!alias || !!linkName;
    const hasRows = !!confirmedOn || !!lastConfirmedOn || !!source;
    return (<SettingsSection name={`${strings.otherNames.itemPrefix} ${index + 1}`}>
        {!!alias && (<SettingsItem title={alias} topDescription={strings.otherNames.sections.alias} icon={<IconUser alt gradientKey="darkPurple"/>}/>)}
        {!!linkName && (<SettingsItem title={linkName} topDescription={strings.otherNames.sections.linkName} icon={<IconUser gradientKey="darkPurple"/>}/>)}
        {hasItems && hasRows ? <DetailSeparator /> : null}
        {!!confirmedOn && (<CardRow title={strings.otherNames.sections.confirmed} info={formatDateForItem(confirmedOn)} small tableSpacing/>)}
        {!!lastConfirmedOn && (<CardRow title={strings.otherNames.sections.lastConfirmed} info={formatDateForItem(lastConfirmedOn)} small tableSpacing/>)}
        {!!source && (<CardRow title={strings.otherNames.sections.source} info={<SourceItem source={source} iconUrl={iconUrl}/>} small tableSpacing/>)}
        {hasRows ? <Spacer.H4 /> : null}
      </SettingsSection>);
});
const renderOtherName = ({ item, index }) => (<OtherNameItem names={item} index={index}/>);
const OtherNames = ({ scrollHandler, contentContainerStyle, }) => {
    const otherNames = useAppSelector(selectCreditReportOtherNames);
    return (<Animated.FlatList data={otherNames} keyExtractor={indexKeyExtractor} renderItem={renderOtherName} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={<SectionSubtitle text={strings.otherNames.headerSubtitle} pulledIn/>} ListEmptyComponent={<ListEmptyView happy onCard title={strings.otherNames.emptyTitle} style={styles.listEmptyView}/>} ListFooterComponent={<DisputeActionCard params={{ category: 'otherNames' }}/>}/>);
};
const SECTION_COMPONENTS = {
    personal: PersonalDetails,
    address: Addresses,
    financialConnections: FinancialConnections,
    otherNames: OtherNames,
};
const CreditReportIdentityDetailScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route }) => {
    const { type } = route.params;
    const fetchCreditReport = useGetCreditReport();
    useEnsureData(selectHasCreditReport, fetchCreditReport);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const SectionComponent = SECTION_COMPONENTS[type];
    return (<AppView withFrame={false}>
          {SectionComponent ? (<SectionComponent scrollHandler={scrollHandler} contentContainerStyle={contentContainerStyle}/>) : null}
        </AppView>);
}, ({ colors, route }) => ({
    title: strings[route.params.type].headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportIdentityDetailScreen;
const styles = StyleSheet.create({
    subtitleContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(8),
        paddingBottom: rem(16),
    },
    subtitleContainerPulledIn: {
        paddingBottom: rem(0),
        marginBottom: rem(-8),
    },
    infoCardMargin: {
        marginTop: rem(16),
    },
    lineSeparator: {
        marginHorizontal: rem(-16),
    },
    sourceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: rem(8),
        marginLeft: rem(32),
    },
    listEmptyView: {
        marginTop: rem(24),
        marginBottom: rem(0),
    },
});
